// Migrations du FORMAT : un document d'une version ancienne est mis au format
// courant à la lecture et à la publication. Une fonction pure par saut
// (n → n + 1). Aucune pour l'instant (format 1).
//
// Ajouter une version : écrire la migration `MIGRATIONS[n]`, monter
// `CURRENT_FORMAT_VERSION` (schema/document.ts) et ajouter un test qui fait
// passer une fixture de la version n à la version n + 1.

import { CURRENT_FORMAT_VERSION, JRAF_FORMAT } from './schema/document';

export type JrafRaw = Record<string, unknown>;
export type Migration = (doc: JrafRaw) => JrafRaw;

/** `MIGRATIONS[n]` transforme un document de la version n en version n + 1. */
export const MIGRATIONS: Readonly<Record<number, Migration>> = {};

export type MigrateResult =
  | { ok: true; doc: JrafRaw; from: number; applied: number[] }
  | {
      ok: false;
      code: 'not_jraf' | 'missing_version' | 'too_new' | 'missing_migration';
      message: string;
    };

export function migrateJraf(
  raw: unknown,
  migrations: Readonly<Record<number, Migration>> = MIGRATIONS,
  current: number = CURRENT_FORMAT_VERSION,
): MigrateResult {
  if (
    !raw ||
    typeof raw !== 'object' ||
    Array.isArray(raw) ||
    (raw as JrafRaw).format !== JRAF_FORMAT
  ) {
    return {
      ok: false,
      code: 'not_jraf',
      message: 'Ce n’est pas une animation JRAF (« format »: "jraf" attendu).',
    };
  }
  const version = (raw as JrafRaw).formatVersion;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    return {
      ok: false,
      code: 'missing_version',
      message: '« formatVersion » manquant ou invalide (entier ≥ 1 attendu).',
    };
  }
  if (version > current) {
    return {
      ok: false,
      code: 'too_new',
      message: `Animation au format ${version}, plus récent que ce lecteur (format ${current}).`,
    };
  }
  let doc: JrafRaw = { ...(raw as JrafRaw) };
  const applied: number[] = [];
  for (let v = version; v < current; v++) {
    const step = migrations[v];
    if (!step) {
      return {
        ok: false,
        code: 'missing_migration',
        message: `Migration du format ${v} vers ${v + 1} introuvable.`,
      };
    }
    doc = { ...step(doc), formatVersion: v + 1 };
    applied.push(v);
  }
  return { ok: true, doc, from: version, applied };
}
